import { Row, Col } from 'antd'
import { useEffect, useState, useContext } from 'react';
import checkNotMobile from '../components/logic/mobile';
import ScreenWidthContext from '../context/screenwidthcontext';
import '../styles.css'
import { webStorage } from '../config';

type ComicPanel = {
    src: string;
    alt: string;
};

const boomerStoryRows: ComicPanel[][] = [
    [
        {
            src: '/story/01.png',
            alt: 'Old grandpa Boomer sits on a park bench with Timmy while zoomers, bots, and hype NFT chaos surround them.',
        },
    ],
    [
        {
            src: '/story/02.png',
            alt: 'Boomer looks irritated by the shenanigans happening around him in the park.',
        },
        {
            src: '/story/03.png',
            alt: 'Boomer glares at bots and whales circling overhead and crowding the mint frenzy.',
        },
    ],
    [
        {
            src: '/story/04.png',
            alt: 'Timmy shows grandpa Boomer an NFT and explains what it is.',
        },
        {
            src: '/story/05.png',
            alt: 'Grandpa Boomer processes the information and starts plotting his own scheme.',
        },
        {
            src: '/story/06.png',
            alt: 'Grandpa Boomer leaves Timmy on the bench and walks away with purpose.',
        },
    ],
    [
        {
            src: '/story/07.png',
            alt: 'A chaotic city scene with Metagas stations, airdropped NFTs, and Boomer arriving at his retirement home.',
        },
    ],
    [
        {
            src: '/story/08.png',
            alt: 'Grandpa Boomer looks over the other residents inside the retirement home.',
        },
        {
            src: '/story/09.png',
            alt: 'Grandpa Boomer gets the full attention of the other boomers.',
        },
    ],
    [
        {
            src: '/story/10.png',
            alt: 'Grandpa Boomer explains what is happening outside to the retirement home residents.',
        },
        {
            src: '/story/11.png',
            alt: 'Grandpa Boomer shares his plan with the Boomer Squad residents.',
        },
    ],
    [
        {
            src: '/story/12.png',
            alt: 'Timmy sits in a quiet, wrecked park that used to be full of zoomer energy.',
        },
    ],
    [
        {
            src: '/story/14.png',
            alt: 'Timmy notices another zoomer carrying coins and crying rainbows.',
        },
        {
            src: '/story/15.png',
            alt: 'Timmy looks over at another zoomer hauling a giant bag of NFTs.',
        },
        {
            src: '/story/16.png',
            alt: 'Timmy stands up in confusion as another zoomer drops his bag and walks away.',
        },
    ],
    [
        {
            src: '/story/17.png',
            alt: 'An empty Metagas station and a transformed Boomer Squad retirement home show how much the world has changed.',
        },
    ],
    [
        {
            src: '/story/18.png',
            alt: 'Boomer Squad comic panel 18.',
        },
        {
            src: '/story/19.png',
            alt: 'Boomer Squad comic panel 19.',
        },
    ],
    [
        {
            src: '/story/20.png',
            alt: 'Boomer Squad comic panel 20.',
        },
    ],
];

const zoomerStoryPanels: ComicPanel[] = [
    {
        src: '/story/zoomer/zoom1.png',
        alt: 'Zoomer story panel 1.',
    },
    {
        src: '/story/zoomer/zoom2.png',
        alt: 'Zoomer story panel 2.',
    },
    {
        src: '/story/zoomer/zoom3.png',
        alt: 'Zoomer story panel 3.',
    },
    {
        src: '/story/zoomer/zoom4.png',
        alt: 'Zoomer story panel 4.',
    },
];

const preloadImage = (src: string) =>
    new Promise<void>((resolve) => {
        const image = new Image();
        const done = () => resolve();

        image.onload = done;
        image.onerror = done;
        image.decoding = 'async';
        image.src = src;

        if (image.complete) {
            done();
        }
    });

export const Comics = () => {
    const { screenWidth } = useContext(ScreenWidthContext)
    const [loading, setLoading] = useState(true)
    const isDesktop = checkNotMobile(screenWidth)

    useEffect(() => {
        let isMounted = true;
        const storyImages = [
            ...boomerStoryRows.flat().map((panel) => webStorage + panel.src),
            ...zoomerStoryPanels.map((panel) => webStorage + panel.src),
        ];

        Promise.all(storyImages.map(preloadImage)).then(() => {
            if (isMounted) {
                setLoading(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const Spinner = () => (
        <div className="sk-fading-circle">
            <div className="sk-circle1 sk-circle"></div>
            <div className="sk-circle2 sk-circle"></div>
            <div className="sk-circle3 sk-circle"></div>
            <div className="sk-circle4 sk-circle"></div>
            <div className="sk-circle5 sk-circle"></div>
            <div className="sk-circle6 sk-circle"></div>
            <div className="sk-circle7 sk-circle"></div>
            <div className="sk-circle8 sk-circle"></div>
            <div className="sk-circle9 sk-circle"></div>
            <div className="sk-circle10 sk-circle"></div>
            <div className="sk-circle11 sk-circle"></div>
            <div className="sk-circle12 sk-circle"></div>
        </div>
    );

    const renderPanel = (panel: ComicPanel, imageClassName: string) => (
        <img
            key={panel.src}
            src={webStorage + panel.src}
            className={imageClassName}
            alt={panel.alt}
            loading="eager"
            decoding="async"
        />
    );

    const renderBoomerRows = (imageClassName: string, rowClassName: string) =>
        boomerStoryRows.map((row, rowIndex) => (
            <Row className={rowClassName} key={`boomer-row-${rowIndex}`}>
                {row.length === 1
                    ? renderPanel(row[0], imageClassName)
                    : row.map((panel) => (
                        <Col flex='1' key={panel.src}>
                            {renderPanel(panel, imageClassName)}
                        </Col>
                    ))}
            </Row>
        ));

    const renderZoomerRows = (imageClassName: string, rowClassName: string) =>
        zoomerStoryPanels.map((panel) => (
            <Row className={rowClassName} key={panel.src}>
                {renderPanel(panel, imageClassName)}
            </Row>
        ));

    const LoadingState = () => (
        <div
            className='zoomer-story'
            style={{ minHeight: isDesktop ? '60vh' : '45vh', padding: isDesktop ? '32px 0 48px' : '20px 0 32px' }}
        >
            <div className={isDesktop ? 'title' : 'mobile-title'}>Comics</div>
            <Row className='loading-container'>
                <Spinner />
            </Row>
        </div>
    );

    const ZoomStory = () => (
        isDesktop
            ? <div className='zoomer-story'>
                <div className="title">Zooomer Story</div>
                <Row className='story-row' justify='center'>
                    {renderZoomerRows('story_img', 'story-image-row')}
                </Row>
            </div>
            : <div>
                <div className="mobile-title">Zooomer Story</div>
                {renderZoomerRows('mobile-story_img', 'mobile-story-image-row')}
            </div>
    );

    const Story = () => (
        <Row justify='center'>
            <div className="title">Booomer Story
                <Row className='story-row'>
                    <Col className='story-image-col'>
                        {renderBoomerRows('story_img', 'story-image-row')}
                    </Col>
                </Row>
            </div>
        </Row>
    );

    const StoryMobile = () => (
        <Row justify='center'>
            <div className="mobile-title">Boomer Story
                <Row className='story-row'>
                    <Col className='mobile-story-image-col'>
                        {renderBoomerRows('mobile-story_img', 'mobile-story-image-row')}
                    </Col>
                </Row>
            </div>
        </Row>
    );

    if (loading) {
        return <LoadingState />;
    }

    return (
        isDesktop
            ? <>
                <Story />
                <ZoomStory />
            </>
            : <>
                <StoryMobile />
                <ZoomStory />
            </>
    )
}
